import { useEffect, useRef } from "react";
import { Reveal } from "./Reveal";
import { Photo } from "./Photo";
import { PHOTOS } from "../images";

const MOMENTS = [
  { key: "morning", src: PHOTOS.momentMorning, caption: "Slow mornings", tone: "sepia", alt: "A slow morning at Dune Villas" },
  { key: "lunch", src: PHOTOS.momentLunch, caption: "Long lunches", tone: "brunswick", alt: "A long lunch shared at Area 83" },
  { key: "pool", src: PHOTOS.momentPool, caption: "Poolside afternoons", tone: "moss", alt: "A poolside afternoon at Dune Villas" },
  { key: "celebration", src: PHOTOS.momentCelebration, caption: "Celebrations that run late", tone: "champagne", alt: "A celebration on the lawns of Area 83" },
];

const SPEED = 0.45;
const RESUME_MS = 2200;

const useAutoDrift = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let paused = false;
    let resumeTimer;
    let raf;
    let pos = el.scrollLeft;

    const loop = () => {
      if (!paused) {
        const first = el.children[0];
        const mid = el.children[MOMENTS.length];
        const loopDist = mid && first ? mid.offsetLeft - first.offsetLeft : 0;
        if (Math.abs(el.scrollLeft - pos) > 2) pos = el.scrollLeft;
        pos += SPEED;
        if (loopDist && pos >= loopDist) pos -= loopDist;
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const pause = () => {
      paused = true;
      clearTimeout(resumeTimer);
    };
    const resumeLater = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        paused = false;
      }, RESUME_MS);
    };
    el.addEventListener("pointerenter", pause);
    el.addEventListener("pointerleave", resumeLater);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resumeLater);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resumeTimer);
      el.removeEventListener("pointerenter", pause);
      el.removeEventListener("pointerleave", resumeLater);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resumeLater);
    };
  }, [ref]);
};

export const Moments = () => {
  const ref = useRef(null);
  useAutoDrift(ref);

  return (
    <section id="experiences" data-testid="experiences-section" className="grain bg-brand-licorice py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-champagne">Experiences</p>
            <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-brand-ivory sm:text-5xl">
              What your stay can look like
            </h2>
          </div>
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-brand-ivory/40">Drag to explore</p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div
          ref={ref}
          data-testid="moments-scroller"
          className="no-scrollbar flex gap-5 overflow-x-auto px-6 sm:px-8 lg:gap-8"
        >
          {[...MOMENTS, ...MOMENTS].map((m, i) => (
            <figure key={`${m.key}-${i}`} className="group w-[76vw] shrink-0 sm:w-[400px] lg:w-[440px]" aria-hidden={i >= MOMENTS.length}>
              <div className="aspect-[3/4] overflow-hidden">
                <Photo
                  src={m.src}
                  alt={m.alt}
                  tone={m.tone}
                  testId={i < MOMENTS.length ? `moment-${m.key}-image` : undefined}
                  imgClassName="transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
              </div>
              <figcaption className="mt-4 font-serif text-2xl font-light italic text-brand-ivory">{m.caption}</figcaption>
            </figure>
          ))}
        </div>
      </Reveal>
    </section>
  );
};
